/**
 * Neon Battle Royale
 * Mobile Sticky Early Bird Booking Bar
 */

(() => {
  'use strict';

  const SELECTOR = '[data-nbr-ticket-sticky-bar]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class NBRTicketStickyBar {
    constructor(element) {
      this.element = element;
      this.sectionId = element.dataset.sectionId || 'default';
      this.bookingTarget =
        element.dataset.bookingTarget || '#early-bird-tickets';

      this.showAfter = Number.parseInt(
        element.dataset.showAfter || '350',
        10
      );

      this.hideNearTarget =
        element.dataset.hideNearTarget === 'true';

      this.allowDismiss =
        element.dataset.allowDismiss === 'true';

      this.button = element.querySelector(
        '[data-nbr-sticky-ticket-button]'
      );

      this.closeButton = element.querySelector(
        '[data-nbr-sticky-ticket-close]'
      );

      this.targetElement = document.querySelector(
        this.bookingTarget
      );

      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionMedia = window.matchMedia(
        REDUCED_MOTION_QUERY
      );

      this.storageKey =
        `nbr-ticket-sticky-dismissed-${this.sectionId}`;

      this.isDismissed = this.getDismissedState();
      this.isTargetVisible = false;
      this.isVisible = false;
      this.scrollTicking = false;
      this.targetObserver = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBookingClick =
        this.handleBookingClick.bind(this);
      this.handleDismiss = this.handleDismiss.bind(this);
      this.handleMediaChange =
        this.handleMediaChange.bind(this);

      this.init();
    }

    init() {
      if (!this.element || this.element.dataset.nbrInitialized) {
        return;
      }

      this.element.dataset.nbrInitialized = 'true';

      this.bindEvents();
      this.observeTicketSection();
      this.update();
    }

    bindEvents() {
      window.addEventListener('scroll', this.handleScroll, {
        passive: true
      });

      window.addEventListener('resize', this.handleResize, {
        passive: true
      });

      if (this.button) {
        this.button.addEventListener(
          'click',
          this.handleBookingClick
        );
      }

      if (this.closeButton) {
        this.closeButton.addEventListener(
          'click',
          this.handleDismiss
        );
      }

      if (typeof this.mobileMedia.addEventListener === 'function') {
        this.mobileMedia.addEventListener(
          'change',
          this.handleMediaChange
        );
      } else {
        this.mobileMedia.addListener(this.handleMediaChange);
      }
    }

    observeTicketSection() {
      if (!this.targetElement || !this.hideNearTarget) {
        return;
      }

      if (!('IntersectionObserver' in window)) {
        return;
      }

      this.targetObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          this.isTargetVisible =
            entry.isIntersecting &&
            entry.intersectionRatio > 0;

          this.update();
        },
        {
          root: null,
          rootMargin: '-10% 0px -25% 0px',
          threshold: [0, 0.01, 0.15]
        }
      );

      this.targetObserver.observe(this.targetElement);
    }

    handleScroll() {
      if (this.scrollTicking) {
        return;
      }

      this.scrollTicking = true;

      window.requestAnimationFrame(() => {
        if (
          this.hideNearTarget &&
          this.targetElement &&
          !this.targetObserver
        ) {
          this.isTargetVisible =
            this.isElementWithinViewport(this.targetElement);
        }

        this.update();
        this.scrollTicking = false;
      });
    }

    handleResize() {
      this.update();
    }

    handleMediaChange() {
      this.update();
    }

    handleBookingClick(event) {
      if (!this.targetElement) {
        return;
      }

      event.preventDefault();

      const scrollBehavior = this.reducedMotionMedia.matches
        ? 'auto'
        : 'smooth';

      this.targetElement.scrollIntoView({
        behavior: scrollBehavior,
        block: 'start'
      });

      if (
        window.history &&
        typeof window.history.replaceState === 'function'
      ) {
        window.history.replaceState(
          null,
          '',
          this.bookingTarget
        );
      }
    }

    handleDismiss() {
      if (!this.allowDismiss) {
        return;
      }

      this.isDismissed = true;
      this.saveDismissedState();
      this.hide(true);
    }

    update() {
      const isMobile = this.mobileMedia.matches;
      const hasScrolledEnough =
        window.scrollY >= this.showAfter;

      const shouldShow =
        isMobile &&
        hasScrolledEnough &&
        !this.isDismissed &&
        !this.isTargetVisible;

      if (shouldShow) {
        this.show();
      } else {
        this.hide(this.isDismissed);
      }
    }

    show() {
      if (this.isVisible) {
        return;
      }

      this.isVisible = true;

      this.element.classList.add('is-visible');
      this.element.classList.remove('is-dismissed');
      this.element.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add(
        'nbr-sticky-ticket-active'
      );
    }

    hide(markDismissed = false) {
      this.isVisible = false;

      this.element.classList.remove('is-visible');

      if (markDismissed) {
        this.element.classList.add('is-dismissed');
      } else {
        this.element.classList.remove('is-dismissed');
      }

      this.element.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove(
        'nbr-sticky-ticket-active'
      );
    }

    isElementWithinViewport(element) {
      const rect = element.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight;

      return (
        rect.top < viewportHeight * 0.9 &&
        rect.bottom > viewportHeight * 0.15
      );
    }

    getDismissedState() {
      if (
        window.Shopify &&
        window.Shopify.designMode
      ) {
        return false;
      }

      try {
        return (
          window.sessionStorage.getItem(this.storageKey) === 'true'
        );
      } catch (error) {
        return false;
      }
    }

    saveDismissedState() {
      if (
        window.Shopify &&
        window.Shopify.designMode
      ) {
        return;
      }

      try {
        window.sessionStorage.setItem(
          this.storageKey,
          'true'
        );
      } catch (error) {
        // The bar still closes for the current page session.
      }
    }

    destroy() {
      window.removeEventListener(
        'scroll',
        this.handleScroll
      );

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      if (this.button) {
        this.button.removeEventListener(
          'click',
          this.handleBookingClick
        );
      }

      if (this.closeButton) {
        this.closeButton.removeEventListener(
          'click',
          this.handleDismiss
        );
      }

      if (typeof this.mobileMedia.removeEventListener === 'function') {
        this.mobileMedia.removeEventListener(
          'change',
          this.handleMediaChange
        );
      } else {
        this.mobileMedia.removeListener(
          this.handleMediaChange
        );
      }

      if (this.targetObserver) {
        this.targetObserver.disconnect();
      }

      this.hide();
      delete this.element.dataset.nbrInitialized;
    }
  }

  const instances = new Map();

  const initializeBars = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((element) => {
      if (instances.has(element)) {
        return;
      }

      const instance = new NBRTicketStickyBar(element);
      instances.set(element, instance);
    });
  };

  const destroyBar = (element) => {
    const bar =
      element.matches?.(SELECTOR)
        ? element
        : element.querySelector?.(SELECTOR);

    if (!bar || !instances.has(bar)) {
      return;
    }

    instances.get(bar).destroy();
    instances.delete(bar);
  };

  const initialize = () => {
    initializeBars(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeBars(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyBar(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => {
      const bar = event.target.querySelector?.(SELECTOR);

      if (!bar) {
        return;
      }

      const instance = instances.get(bar);

      if (!instance) {
        return;
      }

      instance.isDismissed = false;
      instance.update();
    }
  );
})();